<template>
  <div class="chat-widget-wrapper">
    <!-- Chat Toggle Button -->
    <transition name="fade">
      <button 
        v-if="!isOpen"
        @click="openChat"
        class="chat-toggle-btn"
        aria-label="Open Chat"
      >
        <i class="ri-message-3-line"></i>
        <span class="chat-badge" v-if="hasNewMessage">1</span>
      </button>
    </transition>

    <!-- Chat Window -->
    <transition name="slide-up">
      <div v-if="isOpen" class="chat-window">
        <!-- Chat Header -->
        <div class="chat-header">
          <div class="chat-header-info">
            <div class="chat-avatar">
              <i class="ri-user-smile-line"></i>
            </div>
            <div>
              <h4 class="chat-title">{{ config.agent_name }}</h4>
              <p class="chat-status">
                <span class="status-dot"></span>
                Online
              </p>
            </div>
          </div>
          <button @click="closeChat" class="chat-close-btn" aria-label="Close Chat">
            <i class="ri-close-line"></i>
          </button>
        </div>

        <!-- Chat Messages -->
        <div class="chat-messages" ref="messagesContainer">
          <div 
            v-for="(message, index) in messages" 
            :key="index"
            :class="['chat-message', message.type]"
          >
            <div v-if="message.type === 'bot'" class="message-avatar">
              <i class="ri-user-smile-line"></i>
            </div>
            <div class="message-bubble">
              <div class="message-text" v-html="formatMessage(message.text)"></div>
              <div class="message-time">{{ message.time }}</div>
            </div>
          </div>
          
          <!-- Typing Indicator -->
          <div v-if="isTyping" class="chat-message bot">
            <div class="message-avatar">
              <i class="ri-user-smile-line"></i>
            </div>
            <div class="message-bubble typing-indicator">
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>
        </div>

        <!-- Chat Input -->
        <div class="chat-input-wrapper">
          <input
            v-model="userInput"
            @keypress.enter="sendMessage"
            type="text"
            :placeholder="inputPlaceholder"
            class="chat-input"
            :disabled="isTyping"
          />
          <button 
            @click="sendMessage" 
            class="chat-send-btn"
            :disabled="!userInput.trim() || isTyping"
          >
            <i class="ri-send-plane-fill"></i>
          </button>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>

export default {
    name: 'ChatWidget',

    props: {
        /** Override the input placeholder text */
        placeholder: { type: String, default: '' },
    },

    data() {
        return {
            isOpen: true,
            hasNewMessage: false,
            userInput: '',
            messages: [],
            isTyping: false,
            conversationHistory: [],
            hasGreeted: false,

            // Backend-provided config (loaded once on mount)
            config: {
                agent_name: 'AI Assistant',
                general_error_message: '',
            },
        }
    },

    computed: {
        inputPlaceholder() {
            return this.placeholder || 'Type your message...'
        },
    },

    mounted() {
        this.loadConfig()
    },

    methods: {
        // =================================================================
        // CONFIG
        // =================================================================

        async loadConfig() {
            try {
                const { data } = await this.$api.get('public/chatbot/config')
                if (data?.status === 'success') {
                    this.config = { ...this.config, ...data.data }
                }
            } catch (e) {
                console.error('Failed to load chatbot config:', e)
            } finally {
                if (!this.hasGreeted) {
                    this.hasGreeted = true
                    await this.sendToBackend(true)
                }
            }
        },

        // =================================================================
        // CHAT FLOW
        // =================================================================

        openChat() {
            this.isOpen = true
            this.hasNewMessage = false
            if (!this.hasGreeted) {
                this.hasGreeted = true
                this.sendToBackend(true)
            }
        },

        closeChat() {
            this.isOpen = false
        },

        async sendMessage() {
            const text = this.userInput.trim()
            if (!text || this.isTyping) return

            this.userInput = ''
            this.addMessage(text, 'user')

            // Push to local history so the backend receives it
            this.conversationHistory.push({ role: 'user', content: text })

            await this.sendToBackend(false)
        },

        /**
         * Single point of contact with the backend.
         * Sends conversation_history → receives AI message + submission result.
         * The backend handles everything: AI response, data extraction, and
         * appointment creation. The frontend just displays messages.
         */
        async sendToBackend(isInitial) {
            this.isTyping = true

            try {
                const { data } = await this.$api.post('public/chatbot/chat', {
                    conversation_history: this.conversationHistory,
                    is_initial: isInitial,
                })

                if (data?.status !== 'success') throw new Error('Bad response')

                const result = data.data

                // Track conversation history
                this.conversationHistory.push({
                    role: 'assistant',
                    content: result.full_response || result.message,
                })

                // Display AI message
                await this.delay(300)
                this.addMessage(result.message, 'bot')

                // If the backend submitted an appointment, display the submission messages
                if (result.submitted && result.submission_messages?.length) {
                    for (let i = 0; i < result.submission_messages.length; i++) {
                        await this.delay(i === 0 ? 500 : 1500)
                        this.addMessage(result.submission_messages[i], 'bot')
                    }
                }
            } catch (e) {
                console.error('Chat error:', e)
                this.addMessage(
                    this.config.general_error_message || 'An error occurred. Please try again.',
                    'bot'
                )
            } finally {
                this.isTyping = false
            }
        },

        // =================================================================
        // UI HELPERS (pure presentation, no business logic)
        // =================================================================

        addMessage(text, type) {
            this.messages.push({
                text,
                type,
                time: new Date().toLocaleTimeString('en-US', {
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: true,
                }),
            })
            this.$nextTick(() => this.scrollToBottom())
        },

        formatMessage(text) {
            return text.replace(/\n/g, '<br>')
        },

        scrollToBottom() {
            if (this.$refs.messagesContainer) {
                this.$refs.messagesContainer.scrollTop = this.$refs.messagesContainer.scrollHeight
            }
        },

        delay(ms) {
            return new Promise(resolve => setTimeout(resolve, ms))
        },
    },
}
</script>

<style scoped>
.chat-widget-wrapper {
    position: fixed;
    bottom: 20px;
    right: 20px;
    z-index: 9999;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
}

/* Toggle Button */
.chat-toggle-btn {
    width: 60px;
    height: 60px;
    border-radius: 50%;
    background: linear-gradient(135deg, var(--bs-primary), #0056b3);
    border: none;
    color: white;
    font-size: 28px;
    cursor: pointer;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s ease;
    position: relative;
}

.chat-toggle-btn:hover {
    transform: scale(1.1);
    box-shadow: 0 6px 25px rgba(0, 0, 0, 0.3);
}

.chat-badge {
    position: absolute;
    top: -5px;
    right: -5px;
    background: #ff4444;
    color: white;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    font-weight: bold;
    border: 2px solid white;
}

/* Chat Window */
.chat-window {
    width: 380px;
    height: 600px;
    background: white;
    border-radius: 20px;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

/* Chat Header */
.chat-header {
    background: linear-gradient(135deg, var(--bs-primary), #0056b3);
    color: white;
    padding: 20px;
    display: flex;
    align-items: center;
    justify-content: space-between;
}

.chat-header-info {
    display: flex;
    align-items: center;
    gap: 12px;
}

.chat-avatar {
    width: 45px;
    height: 45px;
    border-radius: 50%;
    background: white;
    color: var(--bs-primary);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 24px;
}

.chat-title {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
}

.chat-status {
    margin: 0;
    font-size: 12px;
    display: flex;
    align-items: center;
    gap: 6px;
    opacity: 0.9;
}

.status-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #4ade80;
    animation: pulse 2s infinite;
}

@keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
}

.chat-close-btn {
    background: rgba(255, 255, 255, 0.2);
    border: none;
    color: white;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
    transition: background 0.3s ease;
}

.chat-close-btn:hover {
    background: rgba(255, 255, 255, 0.3);
}

/* Chat Messages */
.chat-messages {
    flex: 1;
    overflow-y: auto;
    padding: 20px;
    background: #f8f9fa;
    display: flex;
    flex-direction: column;
    gap: 15px;
}

.chat-messages::-webkit-scrollbar {
    width: 6px;
}

.chat-messages::-webkit-scrollbar-track {
    background: transparent;
}

.chat-messages::-webkit-scrollbar-thumb {
    background: #cbd5e0;
    border-radius: 10px;
}

.chat-message {
    display: flex;
    gap: 10px;
    animation: messageSlideIn 0.3s ease;
}

@keyframes messageSlideIn {
    from {
        opacity: 0;
        transform: translateY(10px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.chat-message.user {
    flex-direction: row-reverse;
}

.message-avatar {
    width: 35px;
    height: 35px;
    border-radius: 50%;
    background: var(--bs-primary);
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 18px;
    flex-shrink: 0;
}

.message-bubble {
    max-width: 75%;
    padding: 12px 16px;
    border-radius: 18px;
    background: white;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.chat-message.user .message-bubble {
    background: var(--bs-primary);
    color: white;
}

.message-text {
    font-size: 14px;
    line-height: 1.5;
    word-wrap: break-word;
}

.message-text :deep(strong) {
    font-weight: 600;
}

.message-time {
    font-size: 11px;
    opacity: 0.6;
    margin-top: 6px;
}

/* Typing Indicator */
.typing-indicator {
    display: flex;
    gap: 4px;
    align-items: center;
    padding: 12px 16px;
}

.typing-indicator span {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #cbd5e0;
    animation: typing 1.4s infinite;
}

.typing-indicator span:nth-child(2) {
    animation-delay: 0.2s;
}

.typing-indicator span:nth-child(3) {
    animation-delay: 0.4s;
}

@keyframes typing {
    0%, 60%, 100% {
        transform: translateY(0);
    }
    30% {
        transform: translateY(-10px);
    }
}

/* Chat Input */
.chat-input-wrapper {
    padding: 15px 20px;
    background: white;
    border-top: 1px solid #e5e7eb;
    display: flex;
    gap: 10px;
    align-items: center;
}

.chat-input {
    flex: 1;
    padding: 12px 16px;
    border: 1px solid #e5e7eb;
    border-radius: 25px;
    font-size: 14px;
    outline: none;
    transition: border-color 0.3s ease;
}

.chat-input:focus {
    border-color: var(--bs-primary);
}

.chat-input:disabled {
    background: #f3f4f6;
    cursor: not-allowed;
}

.chat-send-btn {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: var(--bs-primary);
    border: none;
    color: white;
    font-size: 18px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s ease;
}

.chat-send-btn:hover:not(:disabled) {
    background: #0056b3;
    transform: scale(1.05);
}

.chat-send-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

/* Transitions */
.fade-enter-active, .fade-leave-active {
    transition: opacity 0.3s ease;
}

.fade-enter-from, .fade-leave-to {
    opacity: 0;
}

.slide-up-enter-active, .slide-up-leave-active {
    transition: all 0.3s ease;
}

.slide-up-enter-from {
    opacity: 0;
    transform: translateY(20px) scale(0.9);
}

.slide-up-leave-to {
    opacity: 0;
    transform: translateY(20px) scale(0.9);
}

/* Responsive */
@media (max-width: 767px) {
    .chat-widget-wrapper {
        bottom: 10px;
        right: 10px;
        left: 10px;
    }
    
    .chat-window {
        width: 100%;
        height: 550px;
    }
    
    .chat-toggle-btn {
        width: 55px;
        height: 55px;
        font-size: 24px;
    }
}
</style>
